import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { RateLimiter, SessionSecurity } from '../lib/security';
import { useError } from '../contexts/ErrorContext';

interface SupabaseContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, userData: any) => Promise<void>;
  signOut: () => Promise<void>;
  isConnected: boolean;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export const SupabaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { showError } = useError();

  useEffect(() => {
    // Check if Supabase is connected
    const checkConnection = async () => {
      try {
        // First check if environment variables are available
        if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
          console.error('Missing Supabase environment variables:', {
            url: import.meta.env.VITE_SUPABASE_URL ? 'Set' : 'Missing',
            key: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Missing'
          });
          setIsConnected(false);
          return;
        }

        // Try a simple health check query with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
        
        try {
          const { data, error } = await supabase
            .from('categories')
            .select('count')
            .limit(1)
            .abortSignal(controller.signal);
          
          clearTimeout(timeoutId);
        
          if (!error && data !== undefined) {
            setIsConnected(true);
          } else {
            console.warn('Supabase connection issue:', {
              error: error?.message || 'Unknown error',
              code: error?.code,
              details: error?.details
            });
            setIsConnected(false);
          }
        } catch (queryError) {
          clearTimeout(timeoutId);
          
          if (queryError.name === 'AbortError') {
            console.warn('Supabase connection timeout after 8 seconds');
          } else {
            console.warn('Supabase query error:', {
              error: queryError instanceof Error ? queryError.message : queryError,
              name: queryError instanceof Error ? queryError.name : 'Unknown'
            });
          }
          setIsConnected(false);
        }
      } catch (error) {
        console.warn('Supabase connection check failed:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          type: typeof error,
          name: error instanceof Error ? error.name : 'Unknown'
        });
        setIsConnected(false);
      }
    };

    checkConnection();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const rateLimitKey = `signin_${email}`;
    
    // Rate limiting: 5 attempts per 15 minutes
    if (!RateLimiter.isAllowed(rateLimitKey, 5, 15 * 60 * 1000)) {
      const error = new Error('Too many sign-in attempts. Please try again in 15 minutes.');
      logger.warn('Sign-in rate limit exceeded', 'Auth', { email });
      throw error;
    }

    try {
      setLoading(true);
      setGlobalError(null);
      
      logger.info('Sign-in attempt started', 'Auth', { email });
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        logger.error('Sign-in failed', 'Auth', { email, error: error.message });
        throw error;
      }
      
      if (data.user) {
        // Initialize session security
        SessionSecurity.initializeSession();
        
        // Reset rate limit on successful login
        RateLimiter.reset(rateLimitKey);
        
        logger.info('Sign-in successful', 'Auth', { 
          userId: data.user.id, 
          email: data.user.email 
        });
      }
      
      return { user: data.user, session: data.session, error: null };
    } catch (error) {
      logger.error('Sign-in error', 'Auth', { email, error });
      setGlobalError('Failed to sign in. Please check your credentials and try again.');
      return { user: null, session: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, userData: any) => {
    const rateLimitKey = `signup_${email}`;
    
    // Rate limiting: 3 attempts per hour
    if (!RateLimiter.isAllowed(rateLimitKey, 3, 60 * 60 * 1000)) {
      const error = new Error('Too many sign-up attempts. Please try again in 1 hour.');
      logger.warn('Sign-up rate limit exceeded', 'Auth', { email });
      throw error;
    }

    try {
      setLoading(true);
      setGlobalError(null);
      
      logger.info('Sign-up attempt started', 'Auth', { email, accountType: userData.accountType });
      
      const { error: signUpError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/signin?verified=true`,
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            phone: userData.phone,
            country: userData.country,
            account_type: userData.accountType
          }
        }
      });
      
      if (signUpError) {
        logger.error('Sign-up failed', 'Auth', { email, error: signUpError.message });
        throw signUpError;
      }
      
      logger.info('Sign-up successful', 'Auth', { email, accountType: userData.accountType });
      
      // Additional user data will be inserted via database triggers or edge functions
    } catch (error) {
      logger.error('Sign-up error', 'Auth', { email, error });
      setGlobalError('Failed to create account. Please try again later.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setGlobalError(null);
      
      const userId = user?.id;
      logger.info('Sign-out attempt started', 'Auth', { userId });
      
      const { error } = await supabase.auth.signOut();
      
      // Clear session security even if Supabase signOut fails
      SessionSecurity.clearSession();
      
      if (error) {
        // Log warning but don't throw error for session cleanup
        logger.warn('Sign-out warning', 'Auth', { userId, error: error.message });
      } else {
        logger.info('Sign-out successful', 'Auth', { userId });
      }
    } catch (error: any) {
      // Handle cases where the session is already missing on the server
      if (error?.message?.includes('session_not_found') || 
          error?.message?.includes('Session from session_id claim in JWT does not exist') ||
          error?.code === 'session_not_found') {
        logger.warn('Session already cleared on server', 'Auth');
        // Clear local session state even if server session is already gone
        setSession(null);
        setUser(null);
        setGlobalError(null);
        return;
      }
      
      logger.error('Sign-out failed', 'Auth', { error });
      setGlobalError('Failed to sign out. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    user,
    loading,
    authLoading,
    signIn,
    signUp,
    signOut,
    isConnected,
    globalError,
    setGlobalError
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};